import type { FC } from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { createPortal } from "react-dom";

import { StoreLocatorFilterButton } from "@locators/components/StoreLocator/components/StoreLocatorFilterButton";

import { ChevronBackwardIcon } from "@jti/brand-icons/chevron-backward";
import { Button } from "@jti/core-components/components/Button";
import { DateRangePicker } from "@jti/ui/components/DatePicker/DateRangePicker";
import { useDrawer } from "@jti/ui/components/Drawer/providers";
import { useBodyScrollLock } from "@jti/ui/hooks/useBodyScrollLock";
import { useDeviceBreakpoint } from "@jti/ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import styles from "./EventsLocatorDateFilter.module.scss";

type EventsLocatorDateFilterProps = {
  selected?: DateRange;
  onApplyDateRange: (range: DateRange | undefined) => void;
  dateRangePickerDropdownAlignment?: "left" | "right";
};

export const EventsLocatorDateFilter: FC<EventsLocatorDateFilterProps> = ({
  selected,
  onApplyDateRange,
  dateRangePickerDropdownAlignment = "left",
}) => {
  const { __ } = useTranslations();
  const { breakpoint } = useDeviceBreakpoint();
  const bodyRef = useRef<HTMLElement | null>(null);
  const { toggleScroll } = useBodyScrollLock();
  const { isOpen, closeDrawer, openDrawer } = useDrawer();
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(selected);

  const isMobileOrTablet = useMemo(() => breakpoint !== "desktop", [breakpoint]);

  useLayoutEffect(() => {
    bodyRef.current = document.body;
  }, []);

  useEffect(() => {
    setDraftRange(selected);
  }, [selected]);

  useEffect(() => {
    toggleScroll(isOpen);
  }, [isOpen, toggleScroll]);

  const handleToggleFilters = () => {
    isOpen ? closeDrawer() : openDrawer();
  };

  const handleApplyDateRange = () => {
    onApplyDateRange(draftRange);
    closeDrawer();
  };

  const dateRangePicker = (
    <DateRangePicker
      selected={isMobileOrTablet ? draftRange : selected}
      onSelect={isMobileOrTablet ? setDraftRange : undefined}
      onSaveSelection={isMobileOrTablet ? setDraftRange : onApplyDateRange}
      dropdownAlignment={dateRangePickerDropdownAlignment}
      applyOnClose={isMobileOrTablet}
      dataTestId="eventsLocatorDateRangePicker"
      className={styles.dateFilterWrapper}
      placeholder={__("events.date_range_picker.placeholder")}
    />
  );

  if (!isMobileOrTablet) {
    return <div className={styles.desktopDateFilter}>{dateRangePicker}</div>;
  }

  return (
    <div>
      <StoreLocatorFilterButton
        isFiltersAvailable={true}
        isFiltersOpened={isOpen}
        onToggleFilters={handleToggleFilters}
        selectedFiltersNumber={selected?.from ? 1 : 0}
      />
      {isOpen &&
        bodyRef.current &&
        createPortal(
          <div className={styles.filtersMobileWrapper}>
            <div className={styles.filtersHeaderContainer}>
              <button className={styles.filtersHeaderButton} onClick={handleToggleFilters}>
                <ChevronBackwardIcon />
                <h2>{__("headerlinks.button.findstore")}</h2>
              </button>
            </div>
            <div className={styles.filtersTitleContainer}>
              <h2 className={styles.filtersTitle} data-testid="eventsLocatorFiltersTitle">
                {__("storelocator.filtersby")}
              </h2>
            </div>
            <div className={styles.filtersBody}>{dateRangePicker}</div>
            <div className={styles.filterButtonContainer}>
              <Button
                variant="primary"
                size={breakpoint === "tablet" ? "md" : "sm"}
                onClick={handleApplyDateRange}
                data-testid="eventsLocatorShowResults"
              >
                {__("storelocator.all_filters.show_button")}
              </Button>
            </div>
          </div>,
          bodyRef.current
        )}
    </div>
  );
};
